import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/EditOutlined";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import EventsIcon from "@material-ui/icons/HistoryOutlined";
import clsx from "clsx";
import { useMenuStyles } from "../../components/menuStyles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import ConnectorIcon from "./ConnectorIcon";
import ConnectorModal from "./ConnectorModal";
import McpConnectorModal from "./McpConnectorModal";
import GoogleConnectorModal from "./GoogleConnectorModal";
import { CATALOG, byType } from "./catalog";
import { connectorHealth, HEALTH_COLOR } from "./connectorHealth";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: theme.spacing(0.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  titleCount: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary },
  tabs: { marginBottom: theme.spacing(2.5), borderBottom: `1px solid ${theme.app.border}`, minHeight: 38 },
  tab: { textTransform: "none", fontSize: 13, fontWeight: 500, minHeight: 38, minWidth: 0 },
  search: { width: "100%", marginBottom: theme.spacing(2) },
  searchInput: { borderRadius: 999, background: "#fff" },
  // card (ref §5.8): logo 28 + nome sans + tipo mono; rodapé com kebab único
  card: {
    background: "#fff",
    border: `1px solid ${theme.app.border}`,
    borderRadius: 10,
    padding: "13px 15px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  cardHover: { cursor: "pointer", transition: "border-color .12s", "&:hover": { borderColor: theme.app.textTertiary } },
  row: { display: "flex", alignItems: "center", gap: 12 },
  info: { minWidth: 0, flex: 1 },
  name: { fontWeight: 500, fontSize: 14, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  type: { fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textTertiary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  desc: { fontSize: 12.5, color: theme.app.textSecondary, marginTop: 2 },
  foot: { display: "flex", alignItems: "center", marginTop: "auto" },
  status: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: theme.app.textSecondary },
  dot: { width: 7, height: 7, borderRadius: "50%", background: theme.app.ok, flexShrink: 0 },
  reconnect: { marginLeft: "auto", textTransform: "none", fontSize: 12, fontWeight: 600, color: theme.app.ink, border: "1px solid #E6E9ED", borderRadius: 8, padding: "2px 10px" },
  kebab: { marginLeft: "auto", color: theme.app.textTertiary, padding: 4 },
  addBadge: { marginLeft: "auto", color: theme.app.textTertiary, display: "flex", alignItems: "center" },
  empty: { padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
}));

const Connectors = () => {
  const history = useHistory();
  const { confirm } = useConfirm();
  const classes = useStyles();
  const menu = useMenuStyles();
  const [tab, setTab] = useState(0);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [connectors, setConnectors] = useState([]);
  const [modal, setModal] = useState({ open: false, item: null, connector: null });
  const [mcpOpen, setMcpOpen] = useState(false); // conector MCP (modal dedicado)
  const [googleOpen, setGoogleOpen] = useState(false); // Google Agenda (OAuth, modal dedicado)
  const [cardMenu, setCardMenu] = useState(null); // { anchor, connector }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/connectors");
      setConnectors(data.connectors || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ?reconnect=<id> (faixa de aviso "WhatsApp desconectado"): abre a conexão para reconectar
  const reconnectId = new URLSearchParams(window.location.search).get("reconnect");
  useEffect(() => {
    if (!reconnectId || !connectors.length) return;
    const c = connectors.find(x => String(x.id) === String(reconnectId));
    if (c) openEdit(c);
    history.replace("/connectors");
  }, [reconnectId, connectors]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = item => {
    if (item && item.type === "mcp") { setMcpOpen(true); return; }
    if (item && item.type === "google") { setGoogleOpen(true); return; }
    setModal({ open: true, item, connector: null });
  };
  const openEdit = c => {
    if (c && c.type === "mcp") { setMcpOpen(true); return; }
    if (c && c.type === "google") { setGoogleOpen(true); return; }
    setModal({ open: true, item: byType(c.type), connector: c });
  };
  const closeModal = () => setModal({ open: false, item: null, connector: null });
  const onSaved = () => {
    closeModal();
    load();
    setTab(0);
  };

  const remove = async c => {
    setCardMenu(null);
    if (!(await confirm({ title: "Remover conector", message: `Remover o conector "${c.name}"?`, confirmText: "Remover", danger: true }))) return;
    try {
      await api.delete(`/connectors/${c.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <Typography className={classes.title}>Conectores</Typography>
          <span className={classes.titleCount}>{connectors.length}</span>
        </div>

        <Tabs className={classes.tabs} value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab className={classes.tab} label={`Meus conectores (${connectors.length})`} />
          <Tab className={classes.tab} label="Todos os conectores" />
        </Tabs>

        {tab === 0 &&
          (connectors.length === 0 ? (
            <div className={classes.empty}>
              Você ainda não configurou nenhum conector. Abra "Todos os conectores" e clique em um para configurar.
            </div>
          ) : (
            <Grid container spacing={2}>
              {connectors.map(c => {
                const cat = byType(c.type) || {};
                const health = connectorHealth(c);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
                    <div className={classes.card}>
                      <div className={classes.row}>
                        <ConnectorIcon icon={cat.icon} name={cat.name || c.type} color={cat.color} size={28} />
                        <div className={classes.info}>
                          <div className={classes.name}>{c.name}</div>
                          <div className={classes.type}>{cat.name || c.type}</div>
                        </div>
                      </div>
                      <div className={classes.foot}>
                        <span className={classes.status} style={health && health.alert ? { color: HEALTH_COLOR[health.state], fontWeight: 600 } : undefined}>
                          <span className={classes.dot} style={health ? { background: HEALTH_COLOR[health.state] } : undefined} />
                          {health ? health.label : "configurado"}
                        </span>
                        {health && health.alert && (
                          <Button size="small" className={classes.reconnect} onClick={() => openEdit(c)}>Reconectar</Button>
                        )}
                        <IconButton size="small" className={classes.kebab} style={health && health.alert ? { marginLeft: 4 } : undefined} onClick={e => setCardMenu({ anchor: e.currentTarget, connector: c })}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          ))}

        {tab === 1 && (
          <>
            <TextField
              className={classes.search}
              placeholder="Buscar conector…"
              variant="outlined"
              size="small"
              value={catalogSearch}
              onChange={e => setCatalogSearch(e.target.value)}
              InputProps={{ className: classes.searchInput, startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            {(() => {
              const q = catalogSearch.trim().toLowerCase();
              const items = q
                ? CATALOG.filter(item =>
                    `${item.name} ${item.description} ${item.type}`.toLowerCase().includes(q)
                  )
                : CATALOG;
              if (items.length === 0) {
                return <div className={classes.empty}>Nenhum conector encontrado para “{catalogSearch}”.</div>;
              }
              return (
                <Grid container spacing={2}>
                  {items.map(item => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item.type}>
                      <div className={`${classes.card} ${classes.cardHover}`} onClick={() => openNew(item)}>
                        <div className={classes.row}>
                          <ConnectorIcon icon={item.icon} name={item.name} color={item.color} size={28} />
                          <div className={classes.info}>
                            <div className={classes.name}>{item.name}</div>
                            <div className={classes.desc}>{item.description}</div>
                          </div>
                          <span className={classes.addBadge}>
                            <AddIcon fontSize="small" />
                          </span>
                        </div>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              );
            })()}
          </>
        )}
      </div>

      <Menu anchorEl={cardMenu ? cardMenu.anchor : null} open={!!cardMenu} onClose={() => setCardMenu(null)} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={() => { const c = cardMenu.connector; setCardMenu(null); openEdit(c); }}>
          <EditIcon fontSize="small" /> Editar
        </MenuItem>
        <MenuItem className={menu.item} onClick={() => { const c = cardMenu.connector; setCardMenu(null); history.push(`/connectors/${c.id}/events`); }}>
          <EventsIcon fontSize="small" /> Ver eventos
        </MenuItem>
        <MenuItem className={clsx(menu.item, menu.danger)} onClick={() => remove(cardMenu.connector)}>
          <DeleteIcon fontSize="small" /> Remover
        </MenuItem>
      </Menu>

      <ConnectorModal
        open={modal.open}
        item={modal.item}
        connector={modal.connector}
        onClose={closeModal}
        onSaved={onSaved}
      />

      <McpConnectorModal
        open={mcpOpen}
        connector={connectors.find(c => c.type === "mcp") || null}
        onClose={() => setMcpOpen(false)}
        onSaved={load}
      />

      <GoogleConnectorModal
        open={googleOpen}
        onClose={() => setGoogleOpen(false)}
        onSaved={load}
      />
    </div>
  );
};

export default Connectors;
